import React from "react";
import { Grid, Typography, Box, Divider, FormControl, Select, MenuItem, OutlinedInput, Tooltip, InputLabel } from "@mui/material";
import { PACKAGE_STATUS_COLOR_MAP } from "../constants/taskStatuses";
import { formatOADateTime } from "../utils/dateUtils";
import { isProcessorLocked as isProcessorLockedFn, isQaLocked as isQaLockedFn, getEligibleUsers } from "../utils/taskTransitions";
import { formatActivityLabel } from "../utils/activityLabels";

/**
 * Detailed Task Panel matching the Figma layout exactly.
 * Split into "Client Details" and "Workflow & Assignments" sections.
 */
const TaskDetailPanel = ({
    task, users, onAssign, onLocalAssign, onStatusChange, permittedTransitions, currentRole, isAssigning,
    // sectionTitle/hiddenFields/additionalFields let callers (e.g. PGS) reuse
    // this section under a different name, drop fields that don't apply to
    // their workflow, and inject their own fields into the same grid. OA
    // Shell's defaults reproduce the original behavior unchanged.
    // (additionalFields is unrelated to task.extraFields, the JSONB column.)
    sectionTitle = "Client Details",
    hiddenFields = [],
    additionalFields = [],
    taskIdLabel = "OA Task ID",
    // Optional resolved client name (e.g. PGS maps its client code to the
    // directory display name); falls back to the raw value on the task.
    clientDisplayName = null
}) => {
    if (!task) return null;

    // Role-capability filter: user is eligible if their role IS the target role,
    // or their role's can_act_as includes the target role ID.
    const processors = getEligibleUsers(users, "Processor");
    const qas = getEligibleUsers(users, "QA");

    const handleProcessorChange = (e) => {
        const raw = e.target.value;
        if (raw === "SKIP_PROCESSING") {
            // Sentinel: Skip Processing selected — set local state to sentinel
            if (onLocalAssign) {
                onLocalAssign.setProcessorId("SKIP_PROCESSING");
            }
            return;
        }
        const val = raw === "" ? null : parseInt(raw);
        if (onLocalAssign) {
            // Admin/AM "To Do" mode: just update local state, don't call API
            onLocalAssign.setProcessorId(val);
        } else if (onAssign) {
            onAssign({ processorId: val, qaId: task.qaId });
        }
    };

    const handleQaChange = (e) => {
        const raw = e.target.value;
        // COL-137: "Not Required" sentinel passes through as a string
        const val = raw === "NOT_REQUIRED" ? "NOT_REQUIRED" : (raw === "" ? null : parseInt(raw));
        if (onLocalAssign) {
            onLocalAssign.setQaId(val);
        } else if (onAssign) {
            onAssign({ processorId: task.processorId, qaId: val });
        }
    };

    const isAdminOrAM = currentRole === "Admin" || currentRole === "Application Manager";

    const resolveName = (id, fallbackName) => {
        if (!id && !fallbackName) return null;
        if (fallbackName && fallbackName !== "No Processor Assigned" && fallbackName !== "No QA Assigned") return fallbackName;
        const user = users?.find(u => u.id === id);
        return user ? user.displayName : null;
    };

    const isSkipProcessing = onLocalAssign?.processorId === "SKIP_PROCESSING";
    // Detect tasks where processing was already skipped (saved with null processorId, status beyond To Do)
    const wasProcessingSkipped = !isSkipProcessing && !task.processorId && task.taskStatus !== 'To Do' && task.processorName === 'Processing Skipped';
    const displayProcessor = isSkipProcessing
        ? "Processing Skipped"
        : (task.processorName === "Processing Skipped" ? "Processing Skipped" : (resolveName(task.processorId, task.processorName) || (task.processorId ? `ID: ${task.processorId}` : "No Processor Assigned")));
    const displayQA = task.qcRequired === false
        ? "Not Required"
        : (resolveName(task.qaId, task.qaName) || (task.qaId ? `ID: ${task.qaId}` : "No QA Assigned"));

    const isProcessorLocked = isProcessorLockedFn(task.taskStatus);
    const isQaLocked = isQaLockedFn(task.taskStatus);

    const isHidden = (key) => hiddenFields.includes(key);

    return (
        <Box sx={{ p: 0, mb: 0, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="oa-task-detail-panel">
            {/* ... rest of the code is unchanged until the assignment section ... */}
            {/* --- Client Details / Task Details Section --- */}
            <Box>
                <Typography fontWeight={600} sx={{ mb: 3, color: '#1a1a2e', fontSize: '16px' }}>
                    {sectionTitle}
                </Typography>
                <Grid container rowSpacing={4} columnSpacing={6}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Application Number
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#111827' }}>
                                {task.applicationNo || "—"}
                            </Typography>
                            {task.packageStatus && (
                                <Tooltip title="Zip Package Generation Status">
                                    <Box sx={{
                                        backgroundColor: (PACKAGE_STATUS_COLOR_MAP[task.packageStatus] || {}).bg || "#F3F4F6",
                                        color: (PACKAGE_STATUS_COLOR_MAP[task.packageStatus] || {}).text || "#374151",
                                        px: 1, py: 0.25, borderRadius: 1,
                                        fontSize: "0.65rem", fontWeight: 700, textTransform: 'uppercase'
                                    }}>
                                        {task.packageStatus}
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Client Name</Typography>
                        {/* PGS tasks carry the client on routed_client rather than
                            client_name, so fall back to it before showing a dash. */}
                        <Typography variant="body2" sx={{ color: '#111827' }}>{clientDisplayName || task.clientName || task.routedClient || "—"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Docket Number</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>{task.docketNo || "—"}</Typography>
                    </Grid>
                    {!isHidden('clientType') && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Client Type</Typography>
                            <Typography variant="body2" sx={{ color: '#111827' }}>{task.clientType || "—"}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Activity Name</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>{formatActivityLabel(task.activityName) || "—"}</Typography>
                    </Grid>
                    {!isHidden('applicationStatus') && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Application Status</Typography>
                            <Typography variant="body2" sx={{ color: '#111827' }}>{task.applicationStatus || "—"}</Typography>
                        </Grid>
                    )}
                    {!isHidden('oaMailDate') && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>OA Mail Date</Typography>
                            <Typography variant="body2" sx={{ color: '#111827' }}>{task.oaMailDate ? formatOADateTime(task.oaMailDate, false) : "—"}</Typography>
                        </Grid>
                    )}
                    {!isHidden('attorneyInitials') && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Attorney Initials</Typography>
                            <Typography variant="body2" sx={{ color: '#111827' }}>{task.attorneyInitials || "—"}</Typography>
                        </Grid>
                    )}
                    {!isHidden('clientFileNumber') && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Client File No.</Typography>
                            <Typography variant="body2" sx={{ color: '#111827' }}>{task.clientFileNumber || "—"}</Typography>
                        </Grid>
                    )}

                    {/* Caller-supplied fields (e.g. PGS-specific detail fields) rendered in the same grid */}
                    {additionalFields.map((f, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={f.key || idx}>
                            <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>{f.label}</Typography>
                            <Box sx={{ mt: 0.25 }}>{f.node}</Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ borderColor: '#CBD5E1', borderBottomWidth: 2, my: 0.5 }} />

            {/* --- Workflow & Assignments Section --- */}
            <Box>
                <Typography fontWeight={600} sx={{ mb: 3, color: '#1a1a2e', fontSize: '16px' }}>
                    Workflow & Assignments
                </Typography>
                <Grid container rowSpacing={4} columnSpacing={6} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>{taskIdLabel}</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>{task.taskId || "—"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Created Date Time</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>
                            {task.createdAt ? formatOADateTime(task.createdAt) : "—"}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Last Updated On</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>
                            {task.updatedAt ? formatOADateTime(task.updatedAt) : "—"}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: '#4B5563', fontWeight: 600, display: 'block', mb: 0.5 }}>Last Updated By</Typography>
                        <Typography variant="body2" sx={{ color: '#111827' }}>{task.updatedBy || "System"}</Typography>
                    </Grid>
                </Grid>

                {/* Inline Selectors */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" variant="outlined">
                            <InputLabel id="task-status-label">Task Status</InputLabel>
                            <Select
                                labelId="task-status-label"
                                label="Task Status"
                                value={task.taskStatus}
                                onChange={(e) => onStatusChange(e.target.value)}
                                disabled={permittedTransitions.length === 0}
                                sx={{ color: '#374151', '&.Mui-disabled': { WebkitTextFillColor: '#374151', opacity: 1, backgroundColor: '#F9FAFB' } }}
                            >
                                <MenuItem value={task.taskStatus}>{task.taskStatus}</MenuItem>
                                {permittedTransitions.map(status => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" variant="outlined">
                            {isAdminOrAM ? (
                                <>
                                    <InputLabel id="processor-label">Select Processor</InputLabel>
                                    <Select
                                        labelId="processor-label"
                                        label="Select Processor"
                                        value={isSkipProcessing ? "SKIP_PROCESSING" : (wasProcessingSkipped ? "PROCESSING_SKIPPED" : (onLocalAssign ? (onLocalAssign.processorId || "") : (task.processorId || "")))}
                                        onChange={handleProcessorChange}
                                        disabled={isAssigning || isProcessorLocked}
                                        sx={{ color: (isSkipProcessing || wasProcessingSkipped || task.processorId) ? 'inherit' : '#9CA3AF' }}
                                    >
                                        <MenuItem value="">
                                            <em>Unassigned</em>
                                        </MenuItem>
                                        {wasProcessingSkipped && (
                                            <MenuItem value="PROCESSING_SKIPPED" disabled sx={{ fontStyle: 'italic', color: '#6366F1' }}>
                                                Processing Skipped
                                            </MenuItem>
                                        )}
                                        {processors.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>{p.displayName}</MenuItem>
                                        ))}
                                    </Select>
                                    {isSkipProcessing && !onLocalAssign?.qaId && (
                                        <Typography variant="caption" sx={{ color: '#EF4444', mt: 0.5, fontStyle: 'italic' }}>
                                            QA selection is required
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                        Processor
                                    </Typography>
                                    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, p: '8.5px 14px', backgroundColor: '#F9FAFB' }}>
                                        <Typography variant="body2" sx={{ color: '#374151' }}>
                                            {displayProcessor}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" variant="outlined">
                            {isAdminOrAM ? (
                                <>
                                    <InputLabel id="qa-label">Select QA</InputLabel>
                                    <Select
                                        labelId="qa-label"
                                        label="Select QA"
                                        value={onLocalAssign ? (onLocalAssign.qaId || "") : (task.qcRequired === false ? "NOT_REQUIRED" : (task.qaId || ""))}
                                        onChange={handleQaChange}
                                        disabled={isAssigning || isQaLocked}
                                        sx={{ color: (task.qaId || task.qcRequired === false) ? 'inherit' : '#9CA3AF' }}
                                    >
                                        <MenuItem value="">
                                            <em>Unassigned</em>
                                        </MenuItem>
                                        <MenuItem value="NOT_REQUIRED" sx={{ fontStyle: 'italic', color: '#6366F1' }}>
                                            Not Required
                                        </MenuItem>
                                        {qas.map((q) => (
                                            <MenuItem key={q.id} value={q.id}>{q.displayName}</MenuItem>
                                        ))}
                                    </Select>
                                </>
                            ) : (
                                <>
                                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                        QA
                                    </Typography>
                                    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, p: '8.5px 14px', backgroundColor: '#F9FAFB' }}>
                                        <Typography variant="body2" sx={{ color: '#374151' }}>
                                            {displayQA}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </Box >
    );
};

export default TaskDetailPanel;
